import { Box, Button, Col, Container, Heading, Row, Text } from '@dbi-tools/ui-kit';
import { FormDatepickerField } from '@dbi-tools/ui-kit/lib/datepicker';
import { Hr } from '@dbi-tools/ui-kit/lib/hr';
import { Img } from '@dbi-tools/ui-kit/lib/img';
import { OptionCard, OptionImage } from '@dbi-tools/ui-kit/lib/options';
import { PreloadImage } from '@dbi-tools/ui-kit/lib/preload-image';
import imgRings from '@quiz-frontend/markup/images/rings.svg';
import { DateTime } from 'luxon';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import { RootState } from '../../store/root-reducer';
import { Question } from '../../types';
import StepsCounter from '../common/steps-counter';
import Control from '../control';
import { DatepickerWrapStyled, ThumbnailImg } from './styled/complex.styled';
import { MainWhiteStyled } from './styled/main.styled';

const ComplexStepLayout: React.FC<{
  question: Question;
  title: string;
  image: string;
  onToggle: (index: number) => void;
  answeredQuestion: number;
  isMobile: boolean;
}> = ({ question, title, image, onToggle, answeredQuestion, isMobile }) => {
  const onClick = (index) => {
    onToggle(index);
  };

  const content = () => {
    if (answeredQuestion === null || !question) {
      return null;
    }

    let desktop = null;
    let mobile = null;

    switch (question.type) {
      case 'date': {
        desktop = (
          <Fragment>
            <DatepickerWrapStyled>
              <FormDatepickerField
                id={question.id}
                label={question.question}
                name={question.name}
                placeholderText="MM/DD/YYYY"
                popperPlacement="bottom-start"
                showMonthDropdown
                showYearDropdown
                autoComplete="off"
                isMobile={isMobile}
                minDate={DateTime.local().toJSDate()}
              />
            </DatepickerWrapStyled>
            <Box align="right">
              <Img bg="transparent" fit="contain" src={imgRings.src} alt="rings" />
            </Box>
          </Fragment>
        );

        mobile = desktop;

        break;
      }
      default:
        desktop = (
          <Fragment>
            <Text>{question.question}</Text>
            <Row>
              <Col md={7}>
                <Row>
                  {question.options.map((option) => (
                    <Col key={option.id} col={6}>
                      <Control question={question} option={option} />
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </Fragment>
        );

        mobile = <Text>{question.question}</Text>;
    }

    return (
      <Fragment>
        <Hr color="secondaryLight" mr={{ xs: 0, lg: 50 }} />
        {isMobile ? mobile : desktop}
      </Fragment>
    );
  };

  const sidebar = () => {
    if (!question) {
      return null;
    }

    let desktop = null;
    let mobile = null;

    switch (question.type) {
      case 'date': {
        desktop = (
          <Box display={{ xs: 'none', lg: 'block' }} mr={-60}>
            <ThumbnailImg>
              <PreloadImage src={image} alt="date" />
            </ThumbnailImg>
          </Box>
        );

        break;
      }
      default:
        desktop = (
          <Box display={{ xs: 'none', lg: 'block' }} mr={-60} mt={-100}>
            {question.options.map((option) => (
              <OptionImage
                key={`desktop_${option.id}`}
                id={`image_${option.id}`}
                value={option.id}
                image={option.image}
                label={option.label}
                name={question.name}
                pb={20}
                imageType="horizontal"
              />
            ))}
          </Box>
        );

        mobile = (
          <Row>
            {question.options.map((option) => (
              <Col sm={6} key={`mobile_${option.id}`}>
                <OptionCard
                  id={option.id}
                  value={option.id}
                  image={option.image}
                  label={option.label}
                  name={question.name}
                  withMobileView
                  pb={20}
                />
              </Col>
            ))}
          </Row>
        );
    }

    return isMobile ? mobile : desktop;
  };

  return (
    <Container>
      <MainWhiteStyled>
        <StepsCounter />
        <Heading measure={{ xs: 'h4', md: 'h3' }} mb={25}>
          {title}
        </Heading>
        <Row>
          <Col lg={8}>
            <Text>Any option is perfectly fine!</Text>
            <Row>
              <Col col={6} md={4} lg={3} xl={2}>
                <Button
                  type="button"
                  variant={answeredQuestion === 0 ? 'solid' : 'outlined'}
                  color={answeredQuestion === 0 ? 'primaryDark' : 'gray500'}
                  fullWidth
                  onClick={() => (answeredQuestion !== 0 ? onClick(0) : null)}
                >
                  yes
                </Button>
              </Col>
              <Col col={6} md={4} lg={3} xl={2}>
                <Button
                  type="button"
                  variant={answeredQuestion === 1 ? 'solid' : 'outlined'}
                  color={answeredQuestion === 1 ? 'primaryDark' : 'gray500'}
                  fullWidth
                  onClick={() => (answeredQuestion !== 1 ? onClick(1) : null)}
                >
                  no
                </Button>
              </Col>
            </Row>
            {content()}
          </Col>
          <Col lg={4}>{sidebar()}</Col>
        </Row>
      </MainWhiteStyled>
    </Container>
  );
};

const mapStateToProps = (state: RootState, ownProps) => ({
  ...ownProps,
  isMobile: state.app.isMobile,
});

export default connect(mapStateToProps)(ComplexStepLayout);
